import {Inject, Injectable} from '@angular/core';
import {OKTA_AUTH} from '@okta/okta-angular';
import {OktaAuth} from '@okta/okta-auth-js';
import {Store} from '@ngrx/store';
import {RxdbRepository} from '../rxdb.repository';
import {Employee} from '../../models/employee/employee.model';
import {StandaloneState} from '../../store/standalone';
import {getEmployee} from '../../store';
import {Subscription} from 'rxjs';
import {bearerFetch, logUpsertErrors, markPulled, shouldPull, watchPending, withTabLock} from './multi-tab-sync';

export const PENDING_EQUOTE_OPPORTUNITIES_SELECTOR = {
    isSynchronized: {$ne: true},
    mainQuoteOfflineId: {$ne: null}
};

export const PENDING_LINKED_QUOTES_SELECTOR = {
    isSynchronized: {$ne: true},
    opportunityOfflineId: {$ne: null}
};

const PULL_BATCH_SIZE = 10;

/**
 * Synchronisation des opportunités eQuote (mode CRM not linked) identique dans tous les onglets :
 * pas de réplication RxDB (liée à l'onglet leader), mais un push/pull sérialisé par Web Locks.
 */
@Injectable({
    providedIn: 'root'
})
export class EQuoteOpportunityCollectionSynchronizer {

    private readonly opportunityCollectionName = 'equote_opportunities';
    private readonly quoteCollectionName = 'quotes';
    private employee: Employee;
    private readonly SIX_MONTHS_MS = 6 * 30 * 24 * 60 * 60 * 1000;
    private pushSubscriptions: Subscription[] = [];

    public constructor(
        public readonly store: Store<StandaloneState>,
        private rxdbRepository: RxdbRepository,
        @Inject(OKTA_AUTH) private _oktaAuth: OktaAuth
    ) {
        this.store.select(getEmployee).subscribe(employee => {
            this.employee = employee;
        });
    }

    async startPush(): Promise<void> {
        if (this.pushSubscriptions.length > 0) {
            return;
        }
        console.log('[EQuoteOpportunityCollectionSynchronizer] Starting live push of opportunities');
        const db = await this.rxdbRepository.initDb();
        this.pushSubscriptions = [
            watchPending(db[this.opportunityCollectionName], PENDING_EQUOTE_OPPORTUNITIES_SELECTOR, () => this.pushPending()),
            // un devis ajouté à une opportunité déjà synchronisée doit aussi déclencher le push
            watchPending(db[this.quoteCollectionName], PENDING_LINKED_QUOTES_SELECTOR, () => this.pushPending())
        ];
    }

    stopPush(): void {
        this.pushSubscriptions.forEach(subscription => subscription.unsubscribe());
        this.pushSubscriptions = [];
    }

    /**
     * Pousse les opportunités et devis eQuote en attente. Un seul onglet à la fois : l'état est relu sous le lock,
     * donc un objet déjà poussé par un autre onglet n'est jamais re-POSTé.
     */
    async pushPending(): Promise<void> {
        await withTabLock('epp-equote-push', async () => {
            const authToken = this._oktaAuth.getAccessToken();
            if (!authToken) {
                throw new Error('[EQuoteOpportunityCollectionSynchronizer] Missing auth token');
            }
            const db = await this.rxdbRepository.initDb();
            const oppCollection = db[this.opportunityCollectionName];
            const quoteCollection = db[this.quoteCollectionName];

            const pendingOpportunities = await oppCollection.find({selector: PENDING_EQUOTE_OPPORTUNITIES_SELECTOR}).exec();
            for (const pendingOpportunity of pendingOpportunities) {
                await this.pushOpportunityWithQuotes(oppCollection, quoteCollection, pendingOpportunity.toMutableJSON(), authToken);
            }

            await this.pushQuotesOfSynchronizedOpportunities(oppCollection, quoteCollection, authToken);
        });
    }

    private async pushOpportunityWithQuotes(oppCollection: any, quoteCollection: any, doc: any, authToken: string): Promise<void> {
        try {
            const serverId = await this.pushOpportunity(doc, authToken);
            if (!serverId) {
                console.warn(`[EQuoteOpportunityCollectionSynchronizer] Live push — opportunity localId=${doc._id}: no serverId returned`);
                return;
            }
            console.log(`[EQuoteOpportunityCollectionSynchronizer] Live push — opportunity localId=${doc._id} → serverId=${serverId}`);

            // Push des quotes liés à cette opportunité AVANT de marquer l'opportunité comme synchronisée
            const unsyncedQuotes: any[] = await quoteCollection
                .find({ selector: { isSynchronized: { $ne: true }, opportunityOfflineId: { $exists: true, $ne: null, $eq: doc.offlineId ?? doc._id } } })
                .exec();
            console.log(`[EQuoteOpportunityCollectionSynchronizer] Live push — ${unsyncedQuotes.length} quote(s) to push for opportunityServerId=${serverId}`);
            for (const quoteDoc of unsyncedQuotes) {
                await this.pushLinkedQuote(quoteCollection, quoteDoc, serverId, authToken);
            }

            // Marquer l'opportunité comme synchronisée uniquement après la synchro complète des quotes
            const existingDoc = await oppCollection.findOne(doc._id).exec();
            if (existingDoc) {
                await existingDoc.incrementalPatch({
                    isSynchronized: true,
                    id: serverId
                });
            }
            console.log(`[EQuoteOpportunityCollectionSynchronizer] Live push — opportunity localId=${doc._id} marked as synchronized after quotes sync`);
        } catch (err) {
            console.error(`[EQuoteOpportunityCollectionSynchronizer] Live push error for opportunity localId=${doc._id}:`, err);
            throw err;
        }
    }

    /**
     * Devis non synchronisés rattachés à une opportunité déjà synchronisée (ex. devis ajouté après coup) :
     * ils sont poussés sous l'id serveur de l'opportunité. Ceux dont l'opportunité n'est pas encore
     * synchronisée sont laissés à pushOpportunityWithQuotes.
     */
    private async pushQuotesOfSynchronizedOpportunities(oppCollection: any, quoteCollection: any, authToken: string): Promise<void> {
        const pendingQuotes: any[] = await quoteCollection.find({selector: PENDING_LINKED_QUOTES_SELECTOR}).exec();
        for (const quoteDoc of pendingQuotes) {
            const opportunity = await oppCollection.findOne({
                selector: {
                    $or: [{_id: quoteDoc.opportunityOfflineId}, {offlineId: quoteDoc.opportunityOfflineId}]
                }
            }).exec();
            if (!opportunity?.isSynchronized || !opportunity?.id) {
                continue;
            }
            await this.pushLinkedQuote(quoteCollection, quoteDoc, opportunity.id, authToken);
        }
    }

    private async pushLinkedQuote(quoteCollection: any, quoteDoc: any, opportunityServerId: string, authToken: string): Promise<void> {
        try {
            const quoteResponse = await this.pushQuote(quoteDoc, opportunityServerId, authToken);
            if (quoteResponse) {
                const existingQuote = await quoteCollection.findOne(quoteDoc._id).exec();
                if (existingQuote) {
                    await existingQuote.incrementalPatch({
                        id: quoteResponse.id,
                        reference: quoteResponse.reference,
                        creationDate: quoteResponse.creationDate,
                        isSynchronized: true
                    });
                }
                console.log(`[EQuoteOpportunityCollectionSynchronizer] Live push — quote localId=${quoteDoc._id} → quoteServerId=${quoteResponse.id}`);
            } else {
                console.warn(`[EQuoteOpportunityCollectionSynchronizer] Live push — quote localId=${quoteDoc._id}: no response returned`);
            }
        } catch (quoteErr) {
            console.error(`[EQuoteOpportunityCollectionSynchronizer] Live push error for quote localId=${quoteDoc._id}:`, quoteErr);
        }
    }

    private async getCheckpointFromCollection(): Promise<string> {
        const defaultCheckpoint = new Date(Date.now() - this.SIX_MONTHS_MS).toISOString();
        try {
            const db = await this.rxdbRepository.initDb();
            const collection = db[this.opportunityCollectionName];
            const docs: any[] = await collection
                .find({ selector: { modificationDate: { $exists: true, $ne: null } } })
                .sort({ modificationDate: 'desc' })
                .limit(1)
                .exec();
            if (docs.length > 0 && docs[0].modificationDate) {
                console.log(`[EQuoteOpportunityCollectionSynchronizer] Checkpoint from RxDB: ${docs[0].modificationDate}`);
                return docs[0].modificationDate;
            }
        } catch (err) {
            console.warn('[EQuoteOpportunityCollectionSynchronizer] Could not read checkpoint from collection, using default:', err);
        }
        console.log(`[EQuoteOpportunityCollectionSynchronizer] No checkpoint in RxDB, using default: ${defaultCheckpoint}`);
        return defaultCheckpoint;
    }

    /**
     * mustWait : attendre un éventuel pull en cours dans un autre onglet et ignorer le throttle
     * (premier sync) ; sinon le pull est simplement sauté si un autre onglet s'en occupe.
     */
    async pull(mustWait: boolean): Promise<void> {
        try {
            await withTabLock('epp-equote-pull', async () => {
                if (!mustWait && !shouldPull(this.opportunityCollectionName)) {
                    return;
                }
                const db = await this.rxdbRepository.initDb();
                const collection = db[this.opportunityCollectionName];
                // checkpoint lu dans IndexedDB, donc déjà partagé entre onglets
                let checkpoint = await this.getCheckpointFromCollection();
                let hasMore = true;
                while (hasMore) {
                    const serverDocs = await this.fetchOpportunitiesPage(checkpoint);
                    const previousCheckpoint = checkpoint;
                    if (serverDocs.length > 0) {
                        // Séparer les devis complets et les insérer dans la collection quotes
                        await this.upsertQuotesFromOpportunities(serverDocs);

                        // Mapper les opportunités sans les quotes (déjà insérées séparément)
                        // Préserver offlineId et standaloneMainQuoteId
                        const newDocs = serverDocs.map(({ offlineId, quotes, ...rest }) => ({ ...rest, _id: offlineId, offlineId }));
                        logUpsertErrors(this.opportunityCollectionName, await collection.bulkUpsert(newDocs));

                        checkpoint = this.maxModificationDate(serverDocs);
                        console.log(`[EQuoteOpportunityCollectionSynchronizer] New checkpoint from pull: ${checkpoint}`);
                    }
                    // garde-fou : sans avancée du checkpoint, une page pleine bouclerait indéfiniment
                    hasMore = serverDocs.length === PULL_BATCH_SIZE && checkpoint !== previousCheckpoint;
                }
                markPulled(this.opportunityCollectionName);
            }, {ifAvailable: !mustWait});
        } catch (error) {
            console.warn(`[EQuoteOpportunityCollectionSynchronizer] Error during HTTP pull for [${this.opportunityCollectionName}]:`, error.message);
        }
    }

    private async fetchOpportunitiesPage(updatedAfter: string): Promise<any[]> {
        const params = new URLSearchParams();
        params.append('modificationDate', updatedAfter);
        params.append('size', PULL_BATCH_SIZE.toString());
        const url = `/apiQuoteManager/v3/opportunities/employee/${encodeURIComponent(this.employee.employeeNumber)}?${params.toString()}`;
        const response = await bearerFetch(url, this._oktaAuth, {method: 'GET'});
        return (await response.json()) || [];
    }

    private maxModificationDate(serverDocs: any[]): string {
        return serverDocs.reduce(
            (max: string, opp: any) => new Date(opp.modificationDate) > new Date(max) ? opp.modificationDate : max,
            serverDocs[0].modificationDate
        );
    }

    private async upsertQuotesFromOpportunities(opportunities: any[]): Promise<void> {
        try {
            const db = await this.rxdbRepository.initDb();
            const quoteCollection = db[this.quoteCollectionName];

            const quotesToUpsert: any[] = [];
            for (const opp of opportunities) {
                if (!opp.quotes || !Array.isArray(opp.quotes)) continue;
                for (const quote of opp.quotes) {
                    if (!quote || !quote.offlineId) continue;
                    quotesToUpsert.push({
                        ...quote,
                        _id: String(quote.offlineId),
                        isSynchronized: true
                    });
                }
            }

            if (quotesToUpsert.length === 0) {
                console.log('[EQuoteOpportunityCollectionSynchronizer] No quotes to upsert from pulled opportunities');
                return;
            }

            console.log(`[EQuoteOpportunityCollectionSynchronizer] Upserting ${quotesToUpsert.length} quote(s) from pulled opportunities into RxDB quotes collection`);
            for (const quoteDoc of quotesToUpsert) {
                await quoteCollection.upsert(quoteDoc);
            }
            console.log(`[EQuoteOpportunityCollectionSynchronizer] ${quotesToUpsert.length} quote(s) upserted into quotes collection`);
        } catch (err) {
            console.error('[EQuoteOpportunityCollectionSynchronizer] Error upserting quotes from pulled opportunities:', err);
        }
    }

    private async pushOpportunity(doc: any, authToken: string): Promise<string | null> {
        const rawData = doc._data ?? (typeof doc.toJSON === 'function' ? doc.toJSON() : { ...doc });
        const body = {
            ...rawData,
            offlineId: rawData.offlineId ?? rawData._id,
            standaloneMainQuoteId: rawData.standaloneMainQuoteId,
        };

        const response = await fetch('/apiQuoteManager/v3/opportunities', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            throw new Error(`[EquoteSync] POST /v3/opportunities returned HTTP ${response.status}`);
        }

        const data = await response.json();
        return data?.id ?? null;
    }

    private async pushQuote(doc: any, opportunityServerId: string, authToken: string): Promise<any | null> {
        // Extraire les données brutes (sans les propriétés internes RxDB circulaires)
        const rawData = doc._data ?? (typeof doc.toJSON === 'function' ? doc.toJSON() : { ...doc });
        const body = {
            ...rawData,
            offlineId: rawData.offlineId ?? rawData._id,
            opportunityOfflineId: rawData.opportunityOfflineId
        };

        const response = await fetch(`/apiQuoteManager/v3/opportunities/${opportunityServerId}/quotes`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            throw new Error(`[EquoteSync] POST /v2/opportunities/${opportunityServerId}/quotes returned HTTP ${response.status}`);
        }

        const data = await response.json();
        if(data.id && data.reference && data.creationDate) {
            return data;
        }
        return null;
    }
}
